/**
 * Approval Service
 * API calls for OKR approval workflow
 */

import { api } from './api';
import type {
  Approval,
  ReviewApprovalRequest,
  BulkApproveRequest,
  BulkApproveResult,
  ApprovalEnabledResponse
} from '../types/approval';
import type { OKR } from '../types/okr';

class ApprovalService {
  /**
   * Check if approval workflow is enabled for current tenant
   */
  async checkApprovalEnabled(): Promise<ApprovalEnabledResponse> {
    const response = await api.get('/approvals/enabled');
    return response.data.data;
  }

  /**
   * Submit OKR for approval
   */
  async submitForApproval(okrId: string): Promise<OKR> {
    const response = await api.post(`/approvals/okrs/${okrId}/submit`);
    return response.data.data.okr;
  }

  /**
   * Get pending approvals for current user (manager)
   */
  async getPendingApprovals(): Promise<OKR[]> {
    const response = await api.get('/approvals/pending');
    return response.data.data.okrs;
  }

  /**
   * Approve OKR
   */
  async approveOKR(okrId: string, comments?: string): Promise<OKR> {
    const data: ReviewApprovalRequest = {
      action: 'approved',
      comments
    };
    const response = await api.post(`/approvals/okrs/${okrId}/review`, data);
    return response.data.data.okr;
  }

  /**
   * Reject OKR
   */
  async rejectOKR(okrId: string, comments?: string): Promise<OKR> {
    const data: ReviewApprovalRequest = {
      action: 'rejected',
      comments
    };
    const response = await api.post(`/approvals/okrs/${okrId}/review`, data);
    return response.data.data.okr;
  }

  /**
   * Bulk approve multiple OKRs
   */
  async bulkApprove(okrIds: string[], comments?: string): Promise<BulkApproveResult[]> {
    const data: BulkApproveRequest = {
      okrIds,
      comments
    };
    const response = await api.post('/approvals/bulk-approve', data);
    return response.data.data.results;
  }

  /**
   * Get approval history for an OKR
   */
  async getApprovalHistory(okrId: string): Promise<Approval[]> {
    const response = await api.get(`/approvals/okrs/${okrId}/history`);
    return response.data.data.approvals;
  }
}

export const approvalService = new ApprovalService();
